import React, {PureComponent} from "react"
import {isEmpty, get} from "lodash"
import {Row, Col} from "react-bootstrap"
import {match as Match} from "react-router-dom"
import PropTypes from "prop-types"
import moment from "moment-timezone"

import Hr from "@irati/ui/src/Layout/Hr"
import {User, Team, UserDateOfBirth, UserInput, UserField} from "@irati/types"
import Form from "@irati/ui/src/Form"
import {Input} from "@irati/ui/src/FormItem"
import Error from "@irati/ui/src/ErrorText"
import Validation from "@irati/utils/validation"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Loading from "@irati/ui/src/Loading"
import {Firstname, Lastname, Email, ExternalID, Office, Teams} from "@irati-web/fields/user"
import EditUserRole from "@irati-web/fields/roles/User"
import userService from "@irati/identity/services/users"
import Button from "@irati/ui/src/Button"
import teamService from "@irati/identity/services/teams"
import {mobileSchema} from "@irati/admin/helpers/validation/schema"
import tracking from "@irati-tracking/admin"
import mfaService from "@irati/auth/services/mfa"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import Flex from "@irati/ui/src/Flex"
import {hangPromise} from "@irati/utils/promise"
import authentication from "@irati/auth"
import {forgotPassword} from "@irati/auth/services/password"
import {officesForTenant, officeForUser} from "@irati/identity/utils/tenant/hasMultipleOffices"
import managerOfTeamLabel from "@irati/identity/helpers/user/managerOfTeamLabel"
import {H2, H4} from "@irati/ui/src/Typography"
import GenericModal from "@irati/ui/src/GenericModal"
import successImage from "@irati/ui/images/success.svg"
import History from "@irati/utils/history"
import workosService from "@irati/workos/services/workos"
import DOBSelect from "@irati/ui/src/DOBSelect"
import {setIntDate} from "@irati/utils/dob"

import BackLink from "../../../components/Users/BackLink"
import {VIEW_PEOPLE} from "../../../routes/routeConfig"
import {ButtonWrapper} from "./styled"
import EditManagersModal from "./EditManagersModal"

export type Option = {
  label: string
  value: string
}

const SSO_MANAGER = "SSO"
const DSYNC_MANAGER = "DIRECTORY_SYNC"

interface State {
  error: string | null
  user: User | null
  originalUser: User | null
  teams: Team[]
  teamOptions: Option[]
  userTeams: Option[]
  managersTeams: Option[]
  load: Promise<any> | null
  saved: boolean
  editManagersModal: boolean
  mfaLoad: Promise<boolean>
  userLoad: Promise<void>
  loadRef?: React.RefObject<any>
  businessUnits: Team[]
  userGroupId?: string | null
  directorySyncId?: string
  ssoId?: string
  dob: string | null
  employmentDate: string | null
}

interface Props extends InjectIntl {
  match: Match
}

class EditUserForm extends PureComponent<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  state: State = {
    error: null,
    user: null,
    originalUser: null,
    teams: [],
    teamOptions: [],
    userTeams: [],
    managersTeams: [],
    load: null,
    saved: false,
    editManagersModal: false,
    dob: null,
    employmentDate: null,
    mfaLoad: mfaService.hasFlag(),
    userLoad: hangPromise()
  }

  saveButton = React.createRef()
  deactivateButton = React.createRef()
  resetPasswordButton = React.createRef()
  loadRef = React.createRef()
  activateButton = React.createRef()

  componentDidMount() {
    const uuid = this.props.match.params.id
    const userLoad = userService.get(uuid, {}, {fetchPolicy: "network-only"}).then(async (user) => {
      const teams = await teamService.list(user.businessId)
      const businessUnits = await officesForTenant()
      const userBusinessUnit = await officeForUser(authentication.business(), user)
      const workOSUser = await workosService.getUser(uuid)

      const teamOptions = teams.map((team: Team) => ({value: team.uuid, label: managerOfTeamLabel(team, user!)}))
      const userTeams = teamOptions.filter(({value}) => user?.teamIds?.includes(value))
      this.setState({
        originalUser: user,
        user,
        teams,
        teamOptions,
        userTeams,
        managersTeams: userTeams,
        dob: this.getUserDate(user?.dateOfBirth),
        employmentDate: user?.externalStartDate ? moment(user?.externalStartDate).format("YYYY-MM-DD") : null,
        businessUnits,
        userGroupId: userBusinessUnit?.uuid,
        directorySyncId: workOSUser.directorySyncId,
        ssoId: workOSUser.ssoId
      })
    })
    this.setState({userLoad})
  }

  getUserDate = (date?: UserDateOfBirth) => {
    if (date?.day && date?.month) {
      const day = date.day.toString().length === 1 ? `0${date?.day}` : date.day
      const month = date.month.toString().length === 1 ? `0${date?.month}` : date.month
      return date?.year ? `${date?.year}-${month}-${day}` : `${month}-${day}`
    }
    return null
  }

  setUserDate = (date?: string) => {
    if (date) {
      return date?.length > 5 ? setIntDate(moment(date).format("DD/MM/YYYY")) : setIntDate(moment(date).format("DD/MM"))
    }
    return setIntDate(date)
  }

  updateUser = (user: User) => {
    this.setState({user})
  }

  handleUpdateRole = async () => {
    const user = await userService.get(this.state.user.uuid, {fetchPolicy: "network-only"})
    tracking.editsRole()
    this.updateUser({...user})
  }

  handleUpdateUser = (label: string, value: string) => {
    this.updateUser({
      ...this.state.user,
      [label]: value
    })
  }

  updateTeams = (value: Option[]) => {
    const {user} = this.state
    tracking.editsTeam()

    const teamIds = value?.map((option: Option) => option.value) || []

    if (user?.primaryTeamId && !value.find((team) => team.value === user.primaryTeamId)) {
      return this.updateUser({
        ...user,
        primaryTeamId: null,
        teamIds
      })
    }
    this.handleUpdateUser("teamIds", teamIds)
    const userTeams = this.state.teamOptions.filter((t) => teamIds.includes(t.value))
    this.setState({userTeams})
  }

  updateUserGroupId = (userGroupId: string) => {
    tracking.editsRegion()
    this.setState({userGroupId})
  }

  handleSave = () => {
    const {formatMessage} = this.props
    const {originalUser, user, userGroupId, dob, employmentDate} = this.state

    Validation.validate()
      .then(async () => {
        if (user && originalUser) {
          if (originalUser.externalUserId !== user.externalUserId) {
            tracking.editUserExternalID(originalUser.uuid)
          }

          if (user.primaryTeamId) {
            tracking.setPrimaryTeam()
          }

          const teamIds: string[] = (get(user, "teamIds", []) || []).filter((t) => !!t)
          const data: UserInput = {
            uuid: user.uuid,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            dateOfBirth: this.setUserDate(dob),
            employmentStartDate: employmentDate ? moment.utc(employmentDate, "YYYY-MM-DD").toISOString() : "",
            teamIds,
            phoneNumber: user.phoneNumber,
            externalUserId: user.externalUserId,
            primaryTeamId: user.primaryTeamId
          }

          this.setState({load: hangPromise(), loadRef: this.saveButton})

          if (userGroupId) {
            await teamService.addUserToTeam(userGroupId, user.uuid)
          }

          const load = userService.update({user: data}).then(() => this.setState({originalUser: user, saved: true}))
          this.setState({load})
        }
      })
      .catch(() => {
        this.setState({load: null})
        this.setState({
          error: formatMessage({id: "admin.people.edituser.validation.error"})
        })
      })
  }

  handleCancel = () => {
    const {originalUser} = this.state
    this.setState({user: originalUser})
  }

  handleMFARetry = () => {
    this.setState({mfaLoad: mfaService.hasFlag()})
  }

  handleResetPassword = () => {
    tracking.resetPassword()
    const load = forgotPassword(this.state.user.email, authentication.business()).then(() => {
      this.setState({saved: true})
    })
    this.loadRef = this.resetPasswordButton
    this.setState({load})
  }

  isActive = () => {
    return this.state.user?.isEnabled || false
  }

  handleActivate = () => {
    tracking.reactivatesUser()
    const {user} = this.state
    const load = userService.activate(user!).then(() => this.setState({user: {...user, isEnabled: true}, saved: true}))
    this.setState({load, loadRef: this.activateButton})
  }

  handleDeactivate = () => {
    tracking.deactivatesUser()
    const {user} = this.state
    const load = userService.deactivate(user!).then(() => this.setState({user: {...user, isEnabled: false}, saved: true}))
    this.setState({load, loadRef: this.deactivateButton})
  }

  handlePopupConfirm = () => {
    History.push(VIEW_PEOPLE, this.context.apps.ADMIN)
  }

  handleSetPrimaryTeam = (value: string) => {
    this.updateUser({
      ...this.state.user,
      primaryTeamId: value
    })
  }

  handleOnHide = (userTeams: Option[]) => {
    const teamOptions = this.state.teamOptions.map((item) => ({
      value: item.value,
      label: userTeams.find((t) => t.value === item.value)?.label || item.label
    }))
    this.setState({teamOptions, userTeams, editManagersModal: false})
  }

  handleManagedFieldsText = (field: UserField) => {
    const {user} = this.state
    const {formatMessage} = this.props
    const fieldManager = user?.managedFields?.fields?.find((f) => f.field === field)?.manager

    switch (fieldManager) {
      case SSO_MANAGER:
        return formatMessage(
          {id: "admin.people.edituser.managedField"},
          {manager: formatMessage({id: "admin.people.edituser.ssoManager"})}
        )
      case DSYNC_MANAGER:
        return formatMessage(
          {id: "admin.people.edituser.managedField"},
          {manager: formatMessage({id: "admin.people.edituser.dsyncManager"})}
        )
      default:
        return ""
    }
  }

  render() {
    const {formatMessage} = this.props
    const {
      error,
      user,
      teams,
      load,
      saved,
      mfaLoad,
      userLoad,
      businessUnits,
      userGroupId,
      directorySyncId,
      ssoId,
      editManagersModal,
      teamOptions,
      userTeams,
      managersTeams,
      dob,
      employmentDate
    } = this.state
    const isManagedExternally = user?.managedFields || !!directorySyncId || !!ssoId

    const placeholder = teamOptions
      ? formatMessage({id: "admin.components.userProfilePopUp.reactSelect.placeholder.select"})
      : formatMessage({id: "admin.components.userProfilePopUp.reactSelect.placeholder.noResultsFound"})

    return (
      <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={this.handleMFARetry} />}>
        <Loading load={userLoad}>
          <BackLink />
          <Row>
            <Col md={10}>
              <Flex justifyContent="space-between" alignItems="center">
                <H2 marginBottom={4}>
                  <FormattedMessage id="admin.people.edituser.title" values={{firstName: user?.firstName, lastName: user?.lastName}} />
                </H2>
                {this.isActive() ? (
                  <Flex alignItems="center">
                    <Button
                      ref={this.resetPasswordButton}
                      onClick={this.handleResetPassword}
                      load={load}
                      loadRef={this.loadRef}
                      marginRight={2}>
                      <FormattedMessage id="platform.resetPassword.button.resetPassword" />
                    </Button>
                    <Button ref={this.deactivateButton} destructive onClick={this.handleDeactivate} load={load} loadRef={this.loadRef}>
                      <FormattedMessage id="admin.components.userProfilePopUp.button.deactivate" />
                    </Button>
                  </Flex>
                ) : (
                  <Button ref={this.activateButton} load={load} onClick={this.handleActivate}>
                    <FormattedMessage id="admin.components.userProfilePopUp.activate" />
                  </Button>
                )}
              </Flex>
            </Col>
          </Row>
          <Row>
            <Col md={8}>
              <Form>
                <H4 marginBottom={2}>
                  <FormattedMessage id="admin.components.personalDetails.title" />
                </H4>
                <Row>
                  <Col xs={6}>
                    <Firstname
                      value={user?.firstName}
                      disabled={!!user?.managedFields?.fields?.find((f) => f.field === UserField.FirstName)}
                      hint={isManagedExternally ? this.handleManagedFieldsText(UserField.FirstName) : null}
                      onChange={(value: string) => this.handleUpdateUser("firstName", value)}
                    />
                  </Col>
                  <Col xs={6}>
                    <Lastname
                      value={user?.lastName}
                      disabled={!!user?.managedFields?.fields?.find((f) => f.field === UserField.LastName)}
                      hint={isManagedExternally ? this.handleManagedFieldsText(UserField.LastName) : null}
                      onChange={(value: string) => this.handleUpdateUser("lastName", value)}
                    />
                  </Col>
                </Row>
                <Email
                  value={user?.email}
                  hint={isManagedExternally ? formatMessage({id: "admin.people.edituser.nonEditable"}) : null}
                  disabled={isManagedExternally}
                  onChange={(value: string) => this.handleUpdateUser("email", value)}
                />
                <Input
                  label="account.profile.fields.mobile.label"
                  value={user?.phoneNumber}
                  name="phone"
                  onChange={(value: string) => this.handleUpdateUser("phoneNumber", value)}
                  validation={mobileSchema()}
                />
                <DOBSelect
                  label={formatMessage({id: "admin.components.personalDetails.dob"})}
                  value={dob}
                  hint={formatMessage({id: "admin.components.personalDetails.dobHint"})}
                  onChange={(value: string) => this.setState({dob: value})}
                  validation={Validation.string().matches(
                    /^\d{2}[-]\d{2}$|^\d{4}[-]\d{2}[-]\d{2}$/,
                    formatMessage({id: "admin.components.personalDetails.dobValidation"})
                  )}
                />
                <Hr />
                <H4 marginBottom={2}>
                  <FormattedMessage id="admin.components.perkboxSettings.title" />
                </H4>
                <DOBSelect
                  disabled={!!user?.managedFields?.fields?.find((f) => f.field === UserField.ExternalStartDate)}
                  label={formatMessage({id: "admin.components.personalDetails.employmentDate"})}
                  value={employmentDate}
                  hint={
                    isManagedExternally
                      ? this.handleManagedFieldsText(UserField.ExternalStartDate)
                      : formatMessage({id: "admin.components.personalDetails.employmentDateHint"})
                  }
                  onChange={(value: string) => this.setState({employmentDate: value})}
                  validation={Validation.string().matches(
                    /^\d{4}[-]\d{2}[-]\d{2}$/,
                    formatMessage({id: "admin.components.personalDetails.employmentDateValidation"})
                  )}
                />
                {!!businessUnits?.length && (
                  <Office
                    value={userGroupId}
                    onChange={this.updateUserGroupId}
                    hint={<FormattedMessage id="platform.fields.country.hint" />}
                  />
                )}
                <EditUserRole user={user} team={null} onChange={this.handleUpdateRole} />
                {userTeams?.length > 0 && (
                  <ButtonWrapper>
                    <Button tertiary cta onClick={() => this.setState({editManagersModal: true})} marginBottom={2}>
                      <FormattedMessage id="admin.components.perkboxSettings.editManagers" />
                    </Button>
                  </ButtonWrapper>
                )}
                {!isEmpty(teams) && (
                  <>
                    <Teams placeholder={placeholder} value={userTeams} onChange={this.updateTeams} options={teamOptions} />
                    {/* <PrimaryTeam
                      value={user?.primaryTeamId}
                      onChange={this.handleSetPrimaryTeam}
                      options={userTeams} /> */}
                  </>
                )}
                <ExternalID
                  disabled={!!user?.managedFields?.fields?.find((f) => f.field === UserField.ExternalUserId)}
                  hint={
                    isManagedExternally
                      ? this.handleManagedFieldsText(UserField.ExternalUserId)
                      : formatMessage({id: "admin.components.userProfilePopUp.form.externalID.hint"})
                  }
                  value={user?.externalUserId}
                  onChange={(value: string) => this.handleUpdateUser("externalUserId", value)}
                />
                <Loading load={load} useLoader={false} />
                {!!error && <Error>{error}</Error>}
                <Flex justifyContent="flex-end" alignItems="center">
                  <Button onClick={this.handleCancel} tertiary marginRight={2}>
                    <FormattedMessage id="account.editProfile.button.cancel" />
                  </Button>
                  <Button ref={this.saveButton} onClick={this.handleSave} load={load} primary>
                    <FormattedMessage id="admin.components.userProfilePopUp.saveButton.text" />
                  </Button>
                </Flex>
              </Form>
            </Col>
          </Row>
        </Loading>
        <GenericModal
          title={formatMessage({id: "account.editProfile.popup.title"}, {name: user?.firstName})}
          critical
          image={successImage}
          show={saved}
          proceedButtonText="account.editProfile.popup.button"
          onHide={this.handlePopupConfirm}
          onConfirm={this.handlePopupConfirm}
          noValidation
        />
        <EditManagersModal showModal={editManagersModal} userTeams={managersTeams} userId={user?.uuid} onHide={this.handleOnHide} />
      </Loading>
    )
  }
}

export default injectIntl(EditUserForm)
